import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Add from '@mui/icons-material/Add';
import Save from '@mui/icons-material/Save';
import Cancel from '@mui/icons-material/Cancel';
import Drafts from '@mui/icons-material/Drafts';
import SearchIcon from '@mui/icons-material/Search';
import { dhis2ConfigAPI, type Dhis2ConfigOut } from '../../services/api';

const DRAFT_KEY_PREFIX = 'form_schema_draft_';

interface SchemaField {
  id: string;
  name?: string;
  type?: string;
  description?: string;
  /** When true, this field is included in the ambient data collection process. */
  consider_for_ambient_data_collection?: boolean;
  parent_group_id?: string;
}

interface SchemaGroup {
  id: string;
  name: string;
  fields?: SchemaField[];
  consider_for_ambient_data_collection?: boolean;
}

interface SchemaSection {
  id: string;
  name: string;
  fields?: SchemaField[];
  groups?: SchemaGroup[];
  /** When true, this entire section is included in the ambient data collection process. */
  consider_for_ambient_data_collection?: boolean;
}

interface FormSchemaEditorProps {
  configs: Dhis2ConfigOut[];
  selectedConfigId: string | null;
  onSelectConfigId: (id: string | null) => void;
}

function matchSchemaSearch(q: string, section: SchemaSection): boolean {
  if (!q.trim()) return true;
  const lower = q.toLowerCase().trim();
  const secMatch = (section.id + ' ' + (section.name ?? '')).toLowerCase().includes(lower);
  if (secMatch) return true;
  for (const f of section.fields ?? []) {
    if ((f.id + ' ' + (f.name ?? '') + ' ' + (f.description ?? '')).toLowerCase().includes(lower)) return true;
  }
  for (const g of section.groups ?? []) {
    if ((g.id + ' ' + (g.name ?? '')).toLowerCase().includes(lower)) return true;
    for (const f of g.fields ?? []) {
      if ((f.id + ' ' + (f.name ?? '') + ' ' + (f.description ?? '')).toLowerCase().includes(lower)) return true;
    }
  }
  return false;
}

export default function FormSchemaEditor({ configs, selectedConfigId, onSelectConfigId }: FormSchemaEditorProps) {
  const [schema, setSchema] = useState<{ sections?: SchemaSection[] }>({ sections: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [searchText, setSearchText] = useState('');

  const draftKey = selectedConfigId ? `${DRAFT_KEY_PREFIX}${selectedConfigId}` : null;

  const loadSchema = async () => {
    if (!selectedConfigId) {
      setSchema({ sections: [] });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await dhis2ConfigAPI.getSchema(selectedConfigId);
      const data = (res.data ?? {}) as { sections?: SchemaSection[] };
      const sections = Array.isArray(data.sections) ? data.sections : [];
      const normalized = sections.map((s) => ({
        ...s,
        consider_for_ambient_data_collection: s.consider_for_ambient_data_collection !== false,
        fields: (s.fields ?? []).map((f) => ({
          ...f,
          consider_for_ambient_data_collection: f.consider_for_ambient_data_collection !== false,
        })),
        groups: (s.groups ?? []).map((g) => ({
          ...g,
          consider_for_ambient_data_collection: g.consider_for_ambient_data_collection !== false,
          fields: (g.fields ?? []).map((f) => ({
            ...f,
            consider_for_ambient_data_collection: f.consider_for_ambient_data_collection !== false,
          })),
        })),
      }));
      setSchema({ sections: normalized });
      if (selectedConfigId) try { sessionStorage.removeItem(DRAFT_KEY_PREFIX + selectedConfigId); } catch { /* ignore */ }
      setHasDraft(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load schema');
      setSchema({ sections: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSchema();
  }, [selectedConfigId]);

  const handleLoadDraft = () => {
    if (!draftKey) return;
    try {
      const raw = sessionStorage.getItem(draftKey);
      if (raw) {
        const data = JSON.parse(raw) as { sections?: SchemaSection[] };
        if (data?.sections) setSchema({ sections: data.sections });
        setHasDraft(true);
      }
    } catch {
      setError('Failed to load draft');
    }
  };

  const handleSaveDraft = () => {
    if (!draftKey) return;
    try {
      sessionStorage.setItem(draftKey, JSON.stringify(schema));
      setHasDraft(true);
    } catch {
      setError('Failed to save draft');
    }
  };

  const handleCancel = () => {
    if (draftKey) try { sessionStorage.removeItem(draftKey); } catch { /* ignore */ }
    setHasDraft(false);
    loadSchema();
  };

  const handleSave = async () => {
    if (!selectedConfigId) return;
    setSaving(true);
    setError(null);
    try {
      await dhis2ConfigAPI.putSchema(selectedConfigId, schema);
      if (draftKey) try { sessionStorage.removeItem(draftKey); } catch { /* ignore */ }
      setHasDraft(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addSection = () => {
    const id = `section_${Date.now()}`;
    setSchema((prev) => ({
      sections: [...(prev.sections ?? []), { id, name: 'New section', fields: [], consider_for_ambient_data_collection: true }],
    }));
  };

  const updateSection = (index: number, field: keyof SchemaSection, value: string | boolean) => {
    setSchema((prev) => {
      const sections = [...(prev.sections ?? [])];
      const sec = sections[index];
      if (sec) {
        (sec as unknown as Record<string, unknown>)[field] = value;
        if (field === 'consider_for_ambient_data_collection' && value === false && sec.fields?.length) {
          sections[index] = {
            ...sec,
            fields: sec.fields.map((f) => ({ ...f, consider_for_ambient_data_collection: false })),
          };
        }
      }
      return { ...prev, sections };
    });
  };

  const addField = (sectionIndex: number) => {
    const id = `field_${Date.now()}`;
    setSchema((prev) => {
      const sections = [...(prev.sections ?? [])];
      const sec = sections[sectionIndex];
      if (sec) {
        sections[sectionIndex] = { ...sec, fields: [...(sec.fields ?? []), { id, name: 'New field', type: 'string', consider_for_ambient_data_collection: true }] };
      }
      return { ...prev, sections };
    });
  };

  const updateField = (sectionIndex: number, fieldIndex: number, key: keyof SchemaField, value: string | boolean) => {
    setSchema((prev) => {
      const sections = [...(prev.sections ?? [])];
      const sec = sections[sectionIndex];
      if (!sec?.fields) return prev;
      const fields = [...sec.fields];
      if (fields[fieldIndex]) (fields[fieldIndex] as unknown as Record<string, unknown>)[key] = value;
      sections[sectionIndex] = { ...sec, fields };
      return { ...prev, sections };
    });
  };

  const updateGroup = (sectionIndex: number, groupIndex: number, field: keyof SchemaGroup, value: string | boolean) => {
    setSchema((prev) => {
      const sections = [...(prev.sections ?? [])];
      const sec = sections[sectionIndex];
      const groups = [...(sec?.groups ?? [])];
      if (groups[groupIndex]) (groups[groupIndex] as unknown as Record<string, unknown>)[field] = value;
      sections[sectionIndex] = { ...sec, groups };
      return { ...prev, sections };
    });
  };

  const updateGroupField = (sectionIndex: number, groupIndex: number, fieldIndex: number, key: keyof SchemaField, value: string | boolean) => {
    setSchema((prev) => {
      const sections = [...(prev.sections ?? [])];
      const sec = sections[sectionIndex];
      const grp = sec?.groups?.[groupIndex];
      if (!grp?.fields) return prev;
      const fields = [...grp.fields];
      if (fields[fieldIndex]) (fields[fieldIndex] as unknown as Record<string, unknown>)[key] = value;
      const groups = [...(sec.groups ?? [])];
      groups[groupIndex] = { ...grp, fields };
      sections[sectionIndex] = { ...sec, groups };
      return { ...prev, sections };
    });
  };

  const addGroup = (sectionIndex: number) => {
    const id = `group_${Date.now()}`;
    setSchema((prev) => {
      const sections = [...(prev.sections ?? [])];
      const sec = sections[sectionIndex];
      sections[sectionIndex] = { ...sec, groups: [...(sec.groups ?? []), { id, name: 'New group', fields: [], consider_for_ambient_data_collection: true }] };
      return { ...prev, sections };
    });
  };

  const addFieldToGroup = (sectionIndex: number, groupIndex: number) => {
    const id = `field_${Date.now()}`;
    setSchema((prev) => {
      const sections = [...(prev.sections ?? [])];
      const sec = sections[sectionIndex];
      const grp = sec?.groups?.[groupIndex];
      if (!grp) return prev;
      const groups = [...(sec.groups ?? [])];
      groups[groupIndex] = { ...grp, fields: [...(grp.fields ?? []), { id, name: 'New field', type: 'string', consider_for_ambient_data_collection: true, parent_group_id: grp.id }] };
      sections[sectionIndex] = { ...sec, groups };
      return { ...prev, sections };
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Config</InputLabel>
          <Select
            value={selectedConfigId ?? ''}
            label="Config"
            onChange={(e) => onSelectConfigId(e.target.value || null)}
          >
            <MenuItem value="">— Select config —</MenuItem>
            {configs.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={!selectedConfigId || saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="outlined" startIcon={<Drafts />} onClick={handleSaveDraft} disabled={!selectedConfigId}>
          Draft
        </Button>
        <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancel} disabled={!selectedConfigId || loading}>
          Cancel
        </Button>
        {draftKey && typeof sessionStorage !== 'undefined' && sessionStorage.getItem(draftKey) != null && (
          <Button size="small" color="secondary" onClick={handleLoadDraft}>
            Restore draft
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!selectedConfigId ? (
        <Typography color="text.secondary">Select a config to edit its form schema (sections and fields).</Typography>
      ) : loading ? (
        <Typography>Loading…</Typography>
      ) : (
        <Paper variant="outlined" sx={{ p: 1 }}>
          <TextField
            size="small"
            placeholder="Search sections, groups, or fields…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} /> }}
            sx={{ mb: 1.5, maxWidth: 400 }}
            fullWidth
          />
          {(schema.sections ?? []).map((sec, si) => {
            if (searchText.trim() && !matchSchemaSearch(searchText, sec)) return null;
            return (
              <Accordion key={sec.id} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sec.consider_for_ambient_data_collection !== false}
                        onChange={(_, checked) => updateSection(si, 'consider_for_ambient_data_collection', checked)}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                      />
                    }
                    label="Use in ambient"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ mr: 2 }}
                  />
                  <TextField
                    size="small"
                    value={sec.name}
                    onChange={(e) => updateSection(si, 'name', e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    label="Section name"
                    sx={{ minWidth: 200 }}
                  />
                  <Typography variant="caption" sx={{ ml: 1, alignSelf: 'center' }} color="text.secondary">
                    ({sec.id})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ display: 'block', overflow: 'auto' }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Standalone fields</Typography>
                  <TableContainer component={Box} sx={{ mb: 2 }}>
                    <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 720 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 120, minWidth: 120 }}>Use in ambient</TableCell>
                          <TableCell sx={{ width: 160 }}>Field ID</TableCell>
                          <TableCell sx={{ width: 200 }}>Name</TableCell>
                          <TableCell sx={{ width: 120 }}>Type</TableCell>
                          <TableCell sx={{ minWidth: 120, maxWidth: 200 }}>Description</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(sec.fields ?? []).map((f, fi) => (
                          <TableRow key={f.id}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={f.consider_for_ambient_data_collection !== false}
                                onChange={(_, checked) => updateField(si, fi, 'consider_for_ambient_data_collection', checked)}
                                size="small"
                                title="Use this field in ambient data collection"
                              />
                            </TableCell>
                            <TableCell><TextField size="small" fullWidth value={f.id} onChange={(e) => updateField(si, fi, 'id', e.target.value)} /></TableCell>
                            <TableCell><TextField size="small" fullWidth value={f.name ?? ''} onChange={(e) => updateField(si, fi, 'name', e.target.value)} /></TableCell>
                            <TableCell><TextField size="small" fullWidth value={f.type ?? 'string'} onChange={(e) => updateField(si, fi, 'type', e.target.value)} /></TableCell>
                            <TableCell><TextField size="small" fullWidth value={f.description ?? ''} onChange={(e) => updateField(si, fi, 'description', e.target.value)} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {(sec.groups ?? []).length > 0 && (
                    <>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Groups (parent data element → child fields)</Typography>
                      {(sec.groups ?? []).map((grp, gi) => (
                        <Accordion key={grp.id} variant="outlined" sx={{ mb: 1.5, '&:before': { display: 'none' } }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={sec.consider_for_ambient_data_collection !== false && (grp.consider_for_ambient_data_collection !== false)}
                                  disabled={sec.consider_for_ambient_data_collection === false}
                                  onChange={(_, checked) => updateGroup(si, gi, 'consider_for_ambient_data_collection', checked)}
                                  onClick={(e) => e.stopPropagation()}
                                  size="small"
                                />
                              }
                              label="Use in ambient"
                              onClick={(e) => e.stopPropagation()}
                              sx={{ mr: 1 }}
                            />
                            <TextField
                              size="small"
                              value={grp.name}
                              onChange={(e) => updateGroup(si, gi, 'name', e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              label="Group (parent DE name)"
                              sx={{ minWidth: 200 }}
                            />
                            <Typography variant="caption" sx={{ ml: 1, alignSelf: 'center' }} color="text.secondary">ID: {grp.id}</Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <TableContainer component={Box}>
                              <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 720 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ width: 120, minWidth: 120 }}>Use in ambient</TableCell>
                                    <TableCell sx={{ width: 160 }}>Field ID</TableCell>
                                    <TableCell sx={{ width: 200 }}>Name</TableCell>
                                    <TableCell sx={{ width: 120 }}>Type</TableCell>
                                    <TableCell sx={{ minWidth: 120, maxWidth: 200 }}>Description</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {(grp.fields ?? []).map((f, fi) => (
                                    <TableRow key={f.id}>
                                      <TableCell padding="checkbox">
                                        <Checkbox
                                          checked={f.consider_for_ambient_data_collection !== false}
                                          onChange={(_, checked) => updateGroupField(si, gi, fi, 'consider_for_ambient_data_collection', checked)}
                                          size="small"
                                          title="Use this field in ambient data collection"
                                        />
                                      </TableCell>
                                      <TableCell><TextField size="small" fullWidth value={f.id} onChange={(e) => updateGroupField(si, gi, fi, 'id', e.target.value)} /></TableCell>
                                      <TableCell><TextField size="small" fullWidth value={f.name ?? ''} onChange={(e) => updateGroupField(si, gi, fi, 'name', e.target.value)} /></TableCell>
                                      <TableCell><TextField size="small" fullWidth value={f.type ?? 'string'} onChange={(e) => updateGroupField(si, gi, fi, 'type', e.target.value)} /></TableCell>
                                        <TableCell><TextField size="small" fullWidth value={f.description ?? ''} onChange={(e) => updateGroupField(si, gi, fi, 'description', e.target.value)} /></TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            <Button size="small" startIcon={<Add />} onClick={() => addFieldToGroup(si, gi)} sx={{ mt: 1 }}>
                              Add field to group
                            </Button>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 1.5 }}>
                    <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => addField(si)}>
                      Add standalone field
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => addGroup(si)}>
                      Add group
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
          <Button startIcon={<Add />} onClick={addSection} sx={{ mt: 1 }}>
            Add section
          </Button>
        </Paper>
      )}
    </Box>
  );
}
